import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import TableBodyRow from "ds/components/Table/Row";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { getItemId } from "ds/components/TableSimple/helpers";
import { TableSimpleStore } from "ds/components/TableSimple/types";

import SpaceDetailsPoliciesBodyPreview from "./BodyPreview";
import SpaceDetailsPoliciesDescriptionCell from "./Cells/DescriptionCell";
import SpaceDetailsPoliciesInheritedFromCell from "./Cells/InheritedFromCell";
import SpaceDetailsPoliciesNameCell from "./Cells/NameCell";
import SpaceDetailsPoliciesTypeCell from "./Cells/TypeCell";
import { SPACE_POLICIES_COLUMN_ID } from "./constants";
import styles from "./styles.module.css";
import { SpaceDetailsPolicy } from "./types";

type SpaceDetailsPoliciesRowsProps = {
  data: SpaceDetailsPolicy[];
  store: TableSimpleStore;
};

const SpaceDetailsPoliciesRows = ({ data, store }: SpaceDetailsPoliciesRowsProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const columns = store(useShallow(selectVisibleColumns));

  const toggleRow = useCallback((itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  return (
    <>
      {data.map((item) => {
        const itemId = getItemId(item, "id");
        const isExpanded = expandedRows.has(itemId);

        return [
          <TableBodyRow
            key={`${itemId}-row`}
            {...keyboardProps}
            className={isExpanded ? styles.expandedRow : undefined}
          >
            {columns.map((column, i) => {
              const cellId = `${itemId}-${column.id}`;
              const cellLayoutProps = {
                id: cellId,
                isFirst: i === 0,
              };

              switch (column.id) {
                case SPACE_POLICIES_COLUMN_ID.NAME:
                  return (
                    <SpaceDetailsPoliciesNameCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                      isExpanded={isExpanded}
                      onToggle={() => toggleRow(itemId)}
                    />
                  );
                case SPACE_POLICIES_COLUMN_ID.DESCRIPTION:
                  return (
                    <SpaceDetailsPoliciesDescriptionCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                    />
                  );
                case SPACE_POLICIES_COLUMN_ID.INHERITED_FROM:
                  return (
                    <SpaceDetailsPoliciesInheritedFromCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                    />
                  );
                case SPACE_POLICIES_COLUMN_ID.TYPE:
                  return (
                    <SpaceDetailsPoliciesTypeCell key={cellId} {...cellLayoutProps} item={item} />
                  );
                default:
                  return null;
              }
            })}
          </TableBodyRow>,
          isExpanded && (
            <SpaceDetailsPoliciesBodyPreview
              key={`${itemId}-preview`}
              body={item.body}
              columnsCount={columns.length}
              keyboardProps={keyboardProps}
            />
          ),
        ];
      })}
    </>
  );
};

export default SpaceDetailsPoliciesRows;
